import { useEffect, useState } from "react";
import { Search, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PageHeader from "@/components/PageHeader";
import HelperHistory from "@/components/HelperHistory";
import PagePerformance from "@/components/PagePerformance";
import { StatusBadge } from "@/components/QueueItem";
import { APPROVAL, ApprovalSelect } from "@/components/Approval";
import api, { errMsg, fmtDate } from "@/lib/api";
import { VIA_LABEL } from "@/components/HelperHistory";

export default function History() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [approval, setApprovalFilter] = useState("all");

  const load = () => api.get("/queue", { params: { bucket: "history" } }).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const undo = async (i) => {
    try {
      await api.post(`/posts/${i.post_id}/targets/${i.tid}/status`, { status: "pending" });
      toast.success("Dikembalikan ke giliran");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const shown = items.filter((i) => (status === "all" || i.status === status) && (approval === "all" || i.approval === approval) && (i.group_name + i.text).toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader eyebrow="Sejarah" title="Rekod post" desc="Semua post yang telah selesai, gagal atau dilangkau, mengikut group." />
      <Tabs defaultValue="all">
        <TabsList className="mb-6 h-auto max-w-full flex-wrap justify-start" data-testid="history-tabs">
          <TabsTrigger value="all" data-testid="history-tab-all">Semua rekod</TabsTrigger>
          <TabsTrigger value="helper" data-testid="history-tab-helper">Berjaya oleh Helper</TabsTrigger>
          <TabsTrigger value="page" data-testid="history-tab-page">Prestasi Page</TabsTrigger>
        </TabsList>
        <TabsContent value="helper"><HelperHistory /></TabsContent>
        <TabsContent value="page"><PagePerformance /></TabsContent>
        <TabsContent value="all">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari group atau teks..." className="h-10 bg-card pl-9" data-testid="history-search-input" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-10 bg-card sm:w-48" data-testid="history-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua status</SelectItem>
            <SelectItem value="done">Selesai</SelectItem>
            <SelectItem value="failed">Gagal</SelectItem>
            <SelectItem value="skipped">Dilangkau</SelectItem>
          </SelectContent>
        </Select>
        <Select value={approval} onValueChange={setApprovalFilter}>
          <SelectTrigger className="h-10 bg-card sm:w-52" data-testid="history-approval-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua kelulusan</SelectItem>
            {Object.entries(APPROVAL).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="panel overflow-x-auto">
        <Table data-testid="history-table">
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Sasaran</TableHead>
              <TableHead>Teks</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Kelulusan admin</TableHead>
              <TableHead>Tarikh</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.length === 0 && (
              <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground" data-testid="history-empty">Belum ada rekod.</TableCell></TableRow>
            )}
            {shown.map((i) => (
              <TableRow key={i.tid} className="border-border" data-testid={`history-row-${i.tid}`}>
                <TableCell className="font-medium">{i.group_name}</TableCell>
                <TableCell className="max-w-md"><p className="line-clamp-2 text-sm text-slate-300">{i.text}</p>{i.error && <p className="mt-1 text-xs text-red-300">{i.error}</p>}</TableCell>
                <TableCell><div className="flex flex-wrap items-center gap-1.5"><StatusBadge status={i.status} />{VIA_LABEL[i.via] && <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary" data-testid={`history-via-${i.tid}`}>{VIA_LABEL[i.via]}</span>}</div></TableCell>
                <TableCell>{i.kind === "group" && i.status === "done" ? <ApprovalSelect item={i} onChange={load} /> : <span className="text-xs text-muted-foreground">-</span>}</TableCell>
                <TableCell className="whitespace-nowrap font-mono text-xs text-muted-foreground">{fmtDate(i.done_at || i.scheduled_at)}</TableCell>
                <TableCell>
                  {i.kind === "group" && (
                    <Button size="sm" variant="ghost" onClick={() => undo(i)} data-testid={`history-undo-btn-${i.tid}`}>
                      <Undo2 className="mr-1.5 h-3.5 w-3.5" /> Kembalikan
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
